import { type KeyboardEvent, useState } from "react";
import { api, type Note, type Person } from "./api.ts";
import { ago } from "./format.ts";
import { ReportThis } from "./ReportThis.tsx";
import { RichText } from "./RichText.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Avatar, Button, TextField } from "./souvenir.ts";

// People's notes, each with its author's face and name: on a place, research, the trip, or someone.
// With `edit`, my own can be changed or removed in place.

export interface NoteEditing {
  trip: string;
  onChanged: () => void;
}

export function NoteList({
  notes,
  members,
  you,
  edit,
}: {
  notes: Note[];
  members: Person[];
  you: string;
  edit?: NoteEditing;
}) {
  // A read-only trip: mine are read like everyone's.
  const readOnly = useReadOnly();
  return (
    <ul className="tm-note-list">
      {notes.map((n) => {
        const person = members.find((m) => m.id === n.by.id);
        return (
          <li key={n.id} className="tm-note">
            <Avatar
              name={n.by.name}
              color={person?.color ?? "sky"}
              {...(person?.src ? { src: person.src } : {})}
              size={28}
            />
            {edit && !readOnly && n.by.id === you ? (
              <MyNote note={n} edit={edit} />
            ) : (
              <div className="tm-note-body">
                <p className="caption tm-muted tm-note-meta">
                  <span className="tm-note-who">{n.by.id === you ? "You" : n.by.name}</span> · {ago(n.at)}
                  {n.edited_at && " · edited"}
                </p>
                <RichText text={n.text} className="callout tm-note-text" />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// One of mine: Edit turns it into a field; Delete removes it (Updates can undo that).
function MyNote({ note, edit }: { note: Note; edit: NoteEditing }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const { busy, error, run } = useSave(edit.onChanged);
  return (
    <div className="tm-note-body">
      <p className="caption tm-muted tm-note-meta">
        <span className="tm-note-who">You</span> · {ago(note.at)}
        {note.edited_at && " · edited"}
      </p>
      {editing ? (
        <form
          className="tm-note-form"
          onSubmit={(e) => {
            e.preventDefault();
            run(() => api.editNote(edit.trip, note.id, text.trim())).then((ok) => ok && setEditing(false));
          }}
          onKeyDown={keepTyping(text !== note.text, () => setEditing(false))}
        >
          <TextField
            label="Your note"
            multiline
            rows={3}
            value={text}
            onChange={(e) => setText((e.target as HTMLTextAreaElement).value)}
          />
          <span className="tm-note-actions">
            <Button variant="secondary" size="sm" type="submit" disabled={busy || !text.trim() || text === note.text}>
              Save
            </Button>
            <Button
              variant="quiet"
              size="sm"
              type="button"
              onClick={() => {
                setText(note.text);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </span>
        </form>
      ) : (
        <>
          <RichText text={note.text} className="callout tm-note-text" />
          <span className="tm-note-actions">
            <Button variant="quiet" size="sm" type="button" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button
              variant="quiet"
              size="sm"
              type="button"
              disabled={busy}
              onClick={() => run(() => api.deleteNote(edit.trip, note.id))}
            >
              Delete
            </Button>
          </span>
        </>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </div>
  );
}

// Writing a note where I'm looking: on an option, research, a place or the trip. Closed, it's one
// quiet "Add a note" button, so cards being compared stay short. None on a read-only trip.
export function AddNote(props: { on: string; edit: NoteEditing; open?: boolean }) {
  return useReadOnly() ? null : <NoteForm {...props} />;
}

function NoteForm({ on, edit, open: startOpen = false }: { on: string; edit: NoteEditing; open?: boolean }) {
  const [open, setOpen] = useState(startOpen);
  const [text, setText] = useState("");
  const { busy, error, run } = useSave(edit.onChanged);
  if (!open)
    return (
      // Its own row, so it sits at the start rather than stretching across a column.
      <div className="tm-add-note">
        <Button variant="quiet" size="sm" type="button" icon="note" data-edit="note" onClick={() => setOpen(true)}>
          Add a note
        </Button>
      </div>
    );
  return (
    <form
      className="tm-note-form"
      onKeyDown={keepTyping(text.trim() !== "", () => !startOpen && setOpen(false))}
      onSubmit={(e) => {
        e.preventDefault();
        run(() => api.addNote(edit.trip, on, text.trim())).then((ok) => {
          if (!ok) return;
          setText("");
          if (!startOpen) setOpen(false);
        });
      }}
    >
      <TextField
        label="Add a note"
        placeholder="What you think, what to check…"
        hint="Your group and their AIs see it."
        multiline
        rows={2}
        value={text}
        onChange={(e) => setText((e.target as HTMLTextAreaElement).value)}
      />
      <span className="tm-note-actions">
        <Button variant="secondary" size="sm" type="submit" disabled={busy || !text.trim()}>
          Save
        </Button>
        {!startOpen && (
          <Button
            variant="quiet"
            size="sm"
            type="button"
            onClick={() => {
              setText("");
              setOpen(false);
            }}
          >
            Cancel
          </Button>
        )}
      </span>
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </form>
  );
}

// Escape in a note being written stays with the note, before the page's own Escape (which closes the card or
// the finding it's on, and the note with it): with nothing written, it puts the note away as Cancel does;
// with something written, it keeps it.
const keepTyping = (written: boolean, cancel: () => void) => (e: KeyboardEvent) => {
  if (e.key !== "Escape") return;
  e.preventDefault();
  e.stopPropagation();
  if (!written) cancel();
};

function useSave(onChanged: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (save: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await save();
      onChanged();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the note. Try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}
